/**
 * The markup tool selector (Phase 7) - sits above the report PDF in the left pane. Kept to
 * four tools + a "done drawing" state, all in the one restrained amber the rest of the
 * app already uses for "needs attention" (see DESIGN.md); no new colour palette.
 */
export type MarkupTool = 'none' | 'highlight' | 'note' | 'rectangle' | 'freehand';

const TOOLS: Array<{ id: MarkupTool; label: string; hint: string }> = [
  { id: 'highlight', label: 'Highlight', hint: 'Drag over a region to highlight it' },
  { id: 'note', label: 'Sticky note', hint: 'Click a point to add a note' },
  { id: 'rectangle', label: 'Box', hint: 'Drag to draw a rectangle' },
  { id: 'freehand', label: 'Pen', hint: 'Draw freehand to circle an area' },
];

interface MarkupToolbarProps {
  activeTool: MarkupTool;
  onSelectTool: (tool: MarkupTool) => void;
  /** Undoes the most recent markup added this session (removes it entirely). */
  onUndo: () => void;
  canUndo: boolean;
}

export function MarkupToolbar({ activeTool, onSelectTool, onUndo, canUndo }: MarkupToolbarProps) {
  return (
    <div className="markup-toolbar" role="toolbar" aria-label="Markup tools">
      {TOOLS.map((tool) => (
        <button
          key={tool.id}
          type="button"
          className={`markup-tool${activeTool === tool.id ? ' markup-tool--active' : ''}`}
          title={tool.hint}
          aria-pressed={activeTool === tool.id}
          onClick={() => onSelectTool(activeTool === tool.id ? 'none' : tool.id)}
        >
          {tool.label}
        </button>
      ))}
      <button
        type="button"
        className="markup-tool"
        title="Remove the last markup you added"
        disabled={!canUndo}
        onClick={onUndo}
      >
        Undo
      </button>
      {activeTool !== 'none' ? (
        <span className="muted markup-toolbar__hint">{TOOLS.find((t) => t.id === activeTool)?.hint}</span>
      ) : null}
    </div>
  );
}
